import { PermissionGuard } from '@/components/PermissionGuard';
import { StatCard } from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { use404Redirect } from '@/hooks/use404Redirect';
import { useExportLines } from '@/hooks/useLineExports';
import { useLines, useLinesSummary } from '@/hooks/useLines';
import { useLinesBillingMap } from '@/hooks/useBilling';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useDebouncedValue } from '@mantine/hooks';
import {
  EXPORT_LINE_COLUMNS,
  type ExportLineColumn,
} from '@octave/contracts';

import { operatorImages } from '@/lib/constants';
import { LINE_TYPE_OPTIONS } from '@/lib/filter-options';
import { statusMapping } from '@/lib/statusMapping';
import { Activity, BarChart3, CreditCard, Plus, Smartphone, Wifi } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { columns } from './lignes/columns';
import { DataTable } from './lignes/data-table';
import { ExportDialog, type ExportFormValues } from './lignes/ExportDialog';

const defaultPageInfo = {
  nextPage: null,
  previousPage: null,
  count: 0,
  nbPage: 0,
  page: 1,
};

export function LinesView() {
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo<{
    status?: string[];
    operator?: string[];
    type?: string[];
    plan?: string[];
  }>(() => {
    const f: {
      status?: string[];
      operator?: string[];
      type?: string[];
      plan?: string[];
    } = {};
    const status = searchParams.getAll('status');
    const operator = searchParams.getAll('operator');
    const type = searchParams.getAll('type');
    const plan = searchParams.getAll('plan');
    if (status.length) f.status = status;
    if (operator.length) f.operator = operator;
    if (type.length) f.type = type;
    if (plan.length) f.plan = plan;
    return f;
  }, [searchParams]);
  const [localSearchText, setLocalSearchText] = useState<string>('');
  const [debouncedSearchText] = useDebouncedValue(localSearchText, 500);

  const { data, isLoading, error, isFetching } = useLines({
    page: pagination.page,
    limit: pagination.limit,
    filters,
    searchText: debouncedSearchText,
  });
  const { data: organization, error: organizationError } = useCurrentOrganization();
  const { data: summary } = useLinesSummary();
  const { data: overageByLine } = useLinesBillingMap();
  const filterOptions = summary?.filterOptions;
  const sortedPlanStats = [...(summary?.planStats || [])].sort((a, b) => b.count - a.count);
  use404Redirect(organizationError);
  const { mutate: exportLines, isPending: isExporting } = useExportLines();

  const pageInfo = data?.pageInfo || defaultPageInfo;
  const lines = data?.lines || [];

  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleFiltersChange = (newFilters: {
    status?: string[];
    operator?: string[];
    type?: string[];
    plan?: string[];
  }) => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('operator');
    params.delete('type');
    params.delete('plan');
    newFilters.status?.forEach((v) => params.append('status', v));
    newFilters.operator?.forEach((v) => params.append('operator', v));
    newFilters.type?.forEach((v) => params.append('type', v));
    newFilters.plan?.forEach((v) => params.append('plan', v));
    setSearchParams(params, { replace: true });
    setPagination({ page: 1, limit: pagination.limit });
  };

  const setSingleFilter = (
    key: 'status' | 'operator' | 'type' | 'plan',
    value: string,
  ) => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('operator');
    params.delete('type');
    params.delete('plan');
    params.append(key, value);
    setSearchParams(params, { replace: true });
    setPagination({ page: 1, limit: pagination.limit });
  };

  const handleSearchChange = (newSearchText: string) => {
    setLocalSearchText(newSearchText);
    setPagination({ page: 1, limit: pagination.limit });
  };

  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  const sites = organization?.sites || [];

  // Table column ids double as export column ids — keep only the exportable ones.
  const availableColumns = [
    ...columns.map((col) => ({
      id: col.id,
      label: col.header as string,
    })),
    { id: 'rio', label: 'RIO' },
    { id: 'simNumber', label: 'N° SIM' },
    { id: 'activationDate', label: 'Date de création' },
  ].filter((col): col is { id: ExportLineColumn; label: string } =>
    (EXPORT_LINE_COLUMNS as readonly string[]).includes(col.id ?? '')
  );

  const handleExport = (data: ExportFormValues) => {
    exportLines(
      {
        sites: data.sites,
        columns: data.columns,
        format: data.format,
        delimiter: data.delimiter || ';',
        includeInactive: data.includeInactive,
      },
      {
        onSuccess: () => {
          setIsExportDialogOpen(false);
          toast.success('Export réussi !');
        },
      }
    );
  };

  const TableSkeleton = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-4 pb-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-32" />
      </div>
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex gap-2">
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Mes Lignes
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Gérez votre parc de lignes téléphoniques
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={() => navigate(`/o/${tenantSlug}/analytics`)}
            >
              <BarChart3 className="h-4 w-4" />
              Analyse
            </Button>
            <ExportDialog
              isOpen={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
              sites={sites}
              availableColumns={availableColumns}
              onExport={handleExport}
              isExporting={isExporting}
            />
            <PermissionGuard permission="write">
              <Button
                size="sm"
                onClick={() => navigate(`/o/${tenantSlug}/lines/new`)}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
              >
                <Plus className="h-3.5 w-3.5" />
                Commander une ligne
              </Button>
            </PermissionGuard>
          </div>
        </div>
        {/* Metric cards */}
        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-6">
            <StatCard
              icon={Activity}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              label="Total lignes"
              value={summary.total}
            >
              {summary.statusCounts && (
                <div className="mt-3 flex flex-wrap gap-x-1 gap-y-1 text-xs text-slate-500">
                  {Object.entries(summary.statusCounts).slice(0, 3).map(([status, count]) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setSingleFilter('status', status)}
                      className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                    >
                      <span className="font-medium text-slate-700">{count}</span>{' '}
                      {statusMapping[status] || status}
                    </button>
                  ))}
                </div>
              )}
            </StatCard>
            <StatCard
              icon={Wifi}
              iconBg="bg-sky-50"
              iconColor="text-sky-600"
              label="Réseaux"
              value={summary.networkStats?.length ?? 0}
            >
              {summary.networkStats && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {summary.networkStats.map((net) => (
                    <button
                      key={net.name}
                      type="button"
                      onClick={() => setSingleFilter('operator', net.name)}
                      className="cursor-pointer flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 transition-colors hover:bg-slate-100"
                    >
                      <img
                        src={operatorImages[net.name] || '/images/operators/default.svg'}
                        alt={net.name}
                        className="h-6 w-6 object-contain"
                      />
                      <span className="text-xs font-medium text-slate-600">{net.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </StatCard>
            <StatCard
              icon={CreditCard}
              iconBg="bg-violet-50"
              iconColor="text-violet-600"
              label="Forfaits"
              value={summary.planStats?.length ?? 0}
            >
              {sortedPlanStats.length > 0 && (
                <div className="mt-3 max-h-28 overflow-y-auto flex flex-col gap-0.5 text-xs text-slate-500">
                  {sortedPlanStats.map((plan) => (
                    <button
                      key={plan.name}
                      type="button"
                      onClick={() => setSingleFilter('plan', plan.name)}
                      className="cursor-pointer text-left rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                    >
                      <span className="font-medium text-slate-700">{plan.count}</span>{' '}
                      {plan.name}
                    </button>
                  ))}
                </div>
              )}
            </StatCard>
            <StatCard
              icon={Smartphone}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="Types de lignes"
              value={summary.typeStats?.length ?? 0}
            >
              {summary.typeStats && (
                <div className="mt-3 flex flex-wrap gap-x-1 gap-y-1 text-xs text-slate-500">
                  {summary.typeStats.map((type) => {
                    const typeValue =
                      LINE_TYPE_OPTIONS.find((opt) => opt.label === type.name)
                        ?.value ?? type.name;
                    return (
                      <button
                        key={type.name}
                        type="button"
                        onClick={() => setSingleFilter('type', typeValue)}
                        className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                      >
                        <span className="font-medium text-slate-700">{type.count}</span>{' '}
                        {type.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </StatCard>
          </div>
        )}

        <div className="mt-6">
          {isLoading ? (
            <div>
              <TableSkeleton />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-red-500 text-lg">
                Une erreur est survenue lors du chargement des données
              </p>
            </div>
          ) : (
            <div>
              <DataTable
                data={lines}
                pagination={pagination}
                pageInfo={pageInfo}
                onPaginationChange={setPagination}
                onFiltersChange={handleFiltersChange}
                onSearchChange={handleSearchChange}
                currentFilters={filters}
                currentSearchText={localSearchText}
                isFetching={isFetching}
                availableFilters={filterOptions}
                overageByLine={overageByLine}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
